import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useConnectorStore } from '@/stores/useConnectorStore';
import { getSharedConnection } from '@/hooks/hubConnection';

// ─── SignalR Payload Type ─────────────────────────────────────────────────────

interface ConnectorSyncStartedPayload {
  connectorId: string;
  connectorName: string;
  workspaceId: string;
}

interface ConnectorSyncCompletedPayload {
  connectorId: string;
  connectorName: string;
  filesNew: number;
  filesModified: number;
  filesDeleted: number;
  workspaceId: string;
}

interface ConnectorSyncErrorPayload {
  connectorId: string;
  connectorName: string;
  error: string;
  workspaceId: string;
}

// ─── Status Dot ───────────────────────────────────────────────────────────────

interface StatusDotProps {
  colorClass: string;
  pulsing?: boolean;
}

function StatusDot({ colorClass, pulsing }: StatusDotProps) {
  return (
    <span
      className={cn(
        'inline-block w-1.5 h-1.5 rounded-full',
        colorClass,
        pulsing && 'animate-pulse motion-reduce:animate-none',
      )}
      aria-hidden
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Compact footer chips that show the sync status of each connector.
 * Subscribes to SignalR ConnectorSyncStarted/Completed/Error events via the
 * shared connection (no new increment — read-only listener).
 */
export function ConnectorStatusChips() {
  const connectors = useConnectorStore((s) => s.connectors);
  const navigate = useNavigate();

  // Track which connectors are currently syncing (from SignalR)
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const syncingIdsRef = useRef(syncingIds);
  syncingIdsRef.current = syncingIds;

  useEffect(() => {
    // Use the shared connection — do NOT increment subscribers here
    const connection = getSharedConnection();
    if (!connection) return;

    let cancelled = false;

    const onStarted = (data: ConnectorSyncStartedPayload) => {
      if (cancelled) return;
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.add(data.connectorId);
        return next;
      });
    };

    const onCompleted = (data: ConnectorSyncCompletedPayload) => {
      if (cancelled) return;
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(data.connectorId);
        return next;
      });
    };

    const onError = (data: ConnectorSyncErrorPayload) => {
      if (cancelled) return;
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(data.connectorId);
        return next;
      });
    };

    connection.on('ConnectorSyncStarted', onStarted);
    connection.on('ConnectorSyncCompleted', onCompleted);
    connection.on('ConnectorSyncError', onError);

    return () => {
      cancelled = true;
      connection.off('ConnectorSyncStarted', onStarted);
      connection.off('ConnectorSyncCompleted', onCompleted);
      connection.off('ConnectorSyncError', onError);
    };
  }, []); // Re-attach whenever the shared connection changes would require polling;
          // in practice, connection-status mounts after connection is established.

  if (connectors.length === 0) return null;

  return (
    <>
      {connectors.map((connector) => {
        const isSyncing = syncingIds.has(connector.id);

        let dotColorClass: string;
        if (!connector.enabled) {
          dotColorClass = 'bg-muted-foreground/50';
        } else if (isSyncing) {
          dotColorClass = 'bg-sopho-entity';
        } else if (connector.lastSyncStatus === 'completed') {
          dotColorClass = 'bg-sopho-process';
        } else if (connector.lastSyncStatus === 'failed') {
          dotColorClass = 'bg-destructive';
        } else {
          // enabled but no status yet
          dotColorClass = 'bg-sopho-rule';
        }

        const tooltipText = isSyncing
          ? `${connector.name}: Synchronisiert…`
          : connector.lastSyncStatus === 'completed'
          ? `${connector.name}: Zuletzt erfolgreich`
          : connector.lastSyncStatus === 'failed'
          ? `${connector.name}: Letzter Sync fehlgeschlagen`
          : connector.enabled
          ? `${connector.name}: Kein Sync-Status`
          : `${connector.name}: Deaktiviert`;

        return (
          <button
            key={connector.id}
            type="button"
            onClick={() => navigate(`/app/connectors/${connector.id}`)}
            aria-label={tooltipText}
            title={tooltipText}
            className={cn(
              'flex items-center gap-1 text-xs font-medium text-muted-foreground',
              'hover:text-foreground transition-colors cursor-pointer',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm',
            )}
          >
            <StatusDot colorClass={dotColorClass} pulsing={isSyncing} />
            <span>{connector.name}</span>
          </button>
        );
      })}
    </>
  );
}
